import React from "react";
import { CardContent } from "@/components/ui/card";
import DashboardCardWrapper from "@/components/common/DashboardCardWrapper";
import DashboardCardHeader from "@/components/common/DashboardCardHeader";
import { Typography } from "@/components/ui/typography";
import BorderedIconWrapper from "../common/BorderIconWrapper";
import { CardSkeleton } from "@/components/ui/skeleton";
import { TruncatedTextTooltip } from "@/components/common/TextTooltip";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { routes } from "@/utils/urls";
import BehavioralIconDark from "@/assets/icons/behavioralIconDark.svg";
import BehavioralIconLight from "@/assets/icons/behavioralIconLight.svg";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import { useFetchCard } from "@/hooks/useFetchCard";

interface DiagnosesData {
  anxiety: boolean;
  depression: boolean;
  suAlcohol: boolean;
  suOther: boolean;
  suOpioids: boolean;
  smi: boolean;
  schizophrenia: boolean;
  dementia: boolean;
  bipolarDisorder: boolean;
  otherMoodDisorders: boolean;
  idd: boolean;
  adhd: boolean;
  eatingDisorders: boolean;
  autism: boolean;
  suicidalIdeations: boolean;
  otherBh: boolean;
}

interface BehavioralHealthData {
  memberId: string;
  diagnoses: DiagnosesData;
  loadDate: string;
}

// Diagnosis display names mapping
const diagnosisLabels: Record<keyof DiagnosesData, string> = {
  anxiety: "Anxiety",
  depression: "Depression",
  suAlcohol: "Substance Use Disorder - Alcohol",
  suOther: "Substance Use Disorder - Other",
  suOpioids: "Substance Use Disorder - Opiods",
  smi: "Serious Mental Illness",
  schizophrenia: "Schizophrenia",
  dementia: "Dementia",
  bipolarDisorder: "Bipolar Disorder",
  otherMoodDisorders: "Other Mood Disorders",
  idd: "I/DD (Intellectual and Developmental Disabilities)",
  adhd: "ADHD",
  eatingDisorders: "Eating Disorders",
  autism: "Autism",
  suicidalIdeations: "Suicidal Ideations",
  otherBh: "Other BH",
};

interface BehavioralHealthCardProps {
  shouldBlink?: boolean;
}

const BehavioralHealthCard: React.FC<BehavioralHealthCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("BEHAVIORAL_HEALTH_DIAGNOSES");
  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  const {
    data: behavioralHealth,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<BehavioralHealthData>({
    endpoint: routes.cards.behavioralHealth,
    memberId,
    processData: (rawData: unknown) =>
      (rawData as { data: BehavioralHealthData }).data,
  });

  const getBehavioralHealth = () => {
    if (!behavioralHealth) return [];

    return Object.entries(behavioralHealth.diagnoses) as [
      keyof DiagnosesData,
      boolean,
    ][];
  };

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
      <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
        <DashboardCardHeader
          icon={
            <BorderedIconWrapper>
              <div className='w-4.5 h-4.5'>
                <img
                  src={BehavioralIconDark}
                  alt='Behavioral Dark'
                  className='block dark:hidden'
                />
                <img
                  src={BehavioralIconLight}
                  alt='Behavioral Light'
                  className='hidden dark:block'
                />
              </div>
            </BorderedIconWrapper>
          }
          title='Behavioral Health Diagnoses'
          className='h-[56px]'
          showStar={true}
          isFavourite={isFavourite}
          onToggleFavourite={toggleFavourite}
        />
        <CardContent className='p-4 md:h-[604px] lg:h-[492px] gap-[12px]'>
          {(() => {
            if (loading) {
              return <CardSkeleton rows={13} showProfile={false} />;
            }

            if (error) {
              return (
                <div className='flex items-center justify-center h-full lg:h-[492px] md:h-[604px]'>
                  <Typography
                    variant='medium-14'
                    className={`${
                      isErrorRed
                        ? "text-red-500 dark:text-red-400"
                        : "text-grey-600 dark:text-grey-500"
                    } font-medium`}>
                    {error}
                  </Typography>
                </div>
              );
            }

            if (!behavioralHealth) {
              return null;
            }

            return (
              <div className='columns-2 gap-[10px] space-y-[10px]'>
                {getBehavioralHealth().map(([key, value]) => {
                  const label = diagnosisLabels[key];
                const isActive = value === true;

                return (
                  <div
                    key={key}
                    className={`
                      flex items-center justify-between gap-1
                      px-4 py-3 rounded-lg
                      border border-grey-300 dark:border-grey-600
                      break-inside-avoid
                    `}>
                    <TruncatedTextTooltip
                      text={label}
                      className='flex-1 min-w-0'>
                      <Typography
                        variant='medium-14'
                        className={`
                          truncate
                          ${
                            isActive
                              ? "text-grey-700 dark:text-grey-400"
                              : "text-grey-500 dark:text-grey-700"
                          }
                        `}>
                        {label}
                      </Typography>
                    </TruncatedTextTooltip>
                    <div className='flex-shrink-0 ml-2'>
                      {isActive ? (
                        <svg
                          width='20'
                          height='20'
                          viewBox='0 0 20 20'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'>
                          <circle cx='10' cy='10' r='10' fill='#4F5B66' />
                          <path
                            d='M14.5 7L8.5 13L5.5 10'
                            className='stroke-white dark:stroke-black'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      ) : (
                        <Typography
                          variant='medium-14'
                          className='text-grey-500 dark:text-grey-700'>
                          --
                        </Typography>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
            );
          })()}
        </CardContent>
      </DashboardCardWrapper>
    </div>
  );
};

export default BehavioralHealthCard;
